/* Prepare at most two selected characters; normal links remain the navigation path. */
(() => {
  const attempted = new Set();
  const pending = new Map();
  const connection = navigator.connection;
  const canPrepare = () => !document.hidden && !connection?.saveData &&
    !/^(slow-)?2g$/.test(connection?.effectiveType || '');
  const afterLoad = (callback) => {
    if (document.readyState === 'complete') callback();
    else window.addEventListener('load', callback, { once: true });
  };

  // Only copy responsive-image attributes, never scripts or event handlers from HTML.
  // A real picture lets the browser choose exactly the same candidate as on detail.
  const warmImage = (original) => new Promise((resolve) => {
    if (!original || !canPrepare()) return resolve();
    const picture = document.createElement('picture');
    picture.hidden = true;
    picture.setAttribute('aria-hidden', 'true');
    for (const source of original.closest('picture')?.querySelectorAll('source') || []) {
      const copy = document.createElement('source');
      for (const key of ['media', 'srcset', 'sizes', 'type', 'width', 'height']) {
        if (source.hasAttribute(key)) copy.setAttribute(key, source.getAttribute(key));
      }
      picture.append(copy);
    }
    const image = new Image();
    image.alt = '';
    image.loading = 'eager';
    image.fetchPriority = 'low';
    const finish = () => {
      clearTimeout(timeout);
      image.onload = image.onerror = null;
      picture.remove();
      resolve();
    };
    const timeout = setTimeout(finish, 5000);
    image.onload = image.onerror = finish;
    // Attach to the picture before assigning URLs so desktop fallback isn't fetched first.
    picture.append(image);
    for (const key of ['sizes', 'srcset', 'src']) {
      if (original.hasAttribute(key)) image.setAttribute(key, original.getAttribute(key));
    }
    document.body.append(picture);
  });

  const prepare = async (link) => {
    if (!canPrepare() || attempted.size >= 2 || !link.isConnected) return;
    const url = new URL(link.href, location.href);
    url.hash = '';
    if (url.origin !== location.origin || attempted.has(url.href)) return;
    attempted.add(url.href);
    const controller = new AbortController();
    pending.set(url.href, controller);
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      // HTTP caching remains browser-controlled, including private/no-store responses.
      // Reading the rendered page preserves character-specific template overrides.
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        priority: 'low',
        signal: controller.signal,
      });
      if (!response.ok || new URL(response.url).origin !== location.origin ||
          !response.headers.get('content-type')?.includes('text/html')) return;
      const html = await response.text();
      if (html.length > 1500000 || !canPrepare()) return;
      const template = document.createElement('template');
      template.innerHTML = html; // Inert: scripts and unrelated media never enter the document.
      const detail = template.content.querySelector('eg-lore-character-detail');
      if (!detail) return;
      await warmImage(detail.querySelector('.eg-lore-character__bg-image'));
      if (!canPrepare()) return;
      await Promise.all([
        warmImage(detail.querySelector('.eg-lore-character__portrait-image')),
        warmImage(detail.querySelector('.eg-lore-character__name-img')),
      ]);
    } catch (_) {
      // Speculation failure must never interfere with navigation.
    } finally {
      clearTimeout(timeout);
      pending.delete(url.href);
    }
  };

  const init = () => {
    const links = [...document.querySelectorAll('a.eg-lore-overview__panel')];
    if (!links.length) return;
    for (const link of links) {
      let hover;
      link.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') hover = setTimeout(() => afterLoad(() => {
          if (link.matches(':hover')) prepare(link);
        }), 150);
      });
      link.addEventListener('pointerleave', () => clearTimeout(hover));
      link.addEventListener('focus', () => afterLoad(() => prepare(link)));
      link.addEventListener('pointerdown', () => afterLoad(() => prepare(link)), { passive: true });
      link.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        link.dataset.navigating = 'true';
      });
    }

    // Mobile has no hover: prepare one prominent, available panel after scrolling settles.
    afterLoad(() => {
      if (!matchMedia('(max-width: 749px)').matches || !('IntersectionObserver' in window) || !canPrepare()) return;
      const visible = new Set();
      let timer;
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.75) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        schedule();
      }, { threshold: [0, 0.75] });
      const schedule = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (!canPrepare() || !visible.size) return;
          const nearest = [...visible].sort((a, b) => {
            const distance = (node) => {
              const rect = node.getBoundingClientRect();
              return Math.abs(rect.top + rect.height / 2 - innerHeight / 2);
            };
            return distance(a) - distance(b);
          })[0];
          prepare(nearest);
          cleanup();
        }, 600);
      };
      const cleanup = () => {
        clearTimeout(timer);
        observer.disconnect();
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('pagehide', cleanup);
      };
      links.forEach((link) => observer.observe(link));
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('pagehide', cleanup, { once: true });
    });
  };
  const cancel = () => pending.forEach((controller) => controller.abort());
  connection?.addEventListener('change', () => { if (!canPrepare()) cancel(); });
  window.addEventListener('pagehide', cancel);
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-navigating]').forEach((link) => delete link.dataset.navigating);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
